import { useMemo, useState } from 'react';
import { LayoutDashboard, Users, Newspaper, Image as ImageIcon, Type, Link2, Settings2, LogOut, Quote, Palette, Globe, Blocks, Search, Bell, ExternalLink, Menu, X, ChevronRight, Megaphone } from 'lucide-react';
import { Overview, LeadsSection, ArticlesSection, StoriesSection, MediaSection, TextsSection, LinksSection, SettingsSection } from './sections';
import { DesignSection, SeoSection } from './design';
import { SectionsSection } from './customSections';
import { PopupsSection } from './popups';
import './admin.css';

const SECTIONS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'leads', label: 'Leads & Enquiries', icon: Users },
  { id: 'articles', label: 'Press & Blogs', icon: Newspaper },
  { id: 'stories', label: 'Alumni Stories', icon: Quote },
  { id: 'sections', label: 'Page Sections', icon: Blocks },
  { id: 'popups', label: 'Popups', icon: Megaphone },
  { id: 'media', label: 'Media Library', icon: ImageIcon },
  { id: 'texts', label: 'Page Texts', icon: Type },
  { id: 'links', label: 'Buttons & Links', icon: Link2 },
  { id: 'design', label: 'Design & Theme', icon: Palette },
  { id: 'seo', label: 'SEO & Tags', icon: Globe },
  { id: 'settings', label: 'Header & Footer', icon: Settings2 },
];

const ACTIVE = { overview: Overview, leads: LeadsSection, articles: ArticlesSection, stories: StoriesSection, sections: SectionsSection, popups: PopupsSection, media: MediaSection, texts: TextsSection, links: LinksSection, design: DesignSection, seo: SeoSection, settings: SettingsSection };

function Sidebar({ section, setSection, onClose }) {
  return (
    <aside className="flex h-full w-[264px] flex-col bg-white">
      <div className="flex items-center justify-between px-5 py-5">
        <div className="flex items-center gap-2.5">
          <img src="/assets/logodark.webp" alt="DITEC" className="h-11 w-auto" />
          <span className="rounded-md bg-[#7367F0]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-[#7367F0]">Admin</span>
        </div>
        {onClose && (
          <button onClick={onClose} aria-label="Close menu" className="flex touch-target items-center justify-center text-[#A5A3AE] hover:text-[#4B465C]">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>
      <p className="px-6 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B9B7C0]">Manage Website</p>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 pb-6">
        {SECTIONS.map((s) => {
          const active = section === s.id;
          return (
            <button
              key={s.id}
              data-testid={`admin-nav-${s.id}`}
              onClick={() => { setSection(s.id); onClose?.(); }}
              className={`group flex w-full touch-target items-center gap-3 rounded-lg px-3.5 py-2.5 text-left text-[13.5px] font-medium transition-all duration-200 ${
                active
                  ? 'bg-[#7367F0] text-white shadow-[0_4px_16px_rgba(115,103,240,0.45)]'
                  : 'text-[#4B465C] hover:translate-x-0.5 hover:bg-[#F8F7FA]'
              }`}
            >
              <s.icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-white' : 'text-[#A5A3AE] group-hover:text-[#7367F0]'}`} />
              <span className="flex-1">{s.label}</span>
              {active && <ChevronRight className="h-4 w-4 text-white/80" />}
            </button>
          );
        })}
      </nav>
      <div className="border-t border-[#EBE9F1] p-4">
        <a href="/" className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-[13px] font-medium text-[#4B465C] transition-colors hover:bg-[#F8F7FA]">
          <ExternalLink className="h-4 w-4 text-[#A5A3AE]" /> View live site
        </a>
      </div>
    </aside>
  );
}

export default function AdminDashboard() {
  const [key, setKey] = useState(sessionStorage.getItem('ditec_admin_key') || '');
  const [authed, setAuthed] = useState(!!sessionStorage.getItem('ditec_admin_key'));
  const [section, setSection] = useState('overview');
  const [error, setError] = useState('');
  const [drawer, setDrawer] = useState(false);
  const [query, setQuery] = useState('');

  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    return SECTIONS.filter((s) => s.label.toLowerCase().includes(query.toLowerCase()));
  }, [query]);

  const login = (e) => {
    e.preventDefault();
    if (!key.trim()) return;
    sessionStorage.setItem('ditec_admin_key', key.trim());
    setAuthed(true);
    setError('');
  };

  const logout = () => {
    sessionStorage.removeItem('ditec_admin_key');
    setAuthed(false);
    setKey('');
  };

  if (!authed) {
    return (
      <div data-testid="admin-login" className="admin-shell flex min-h-screen items-center justify-center px-4">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-[#EBE9F1] bg-white p-8 shadow-[0_8px_30px_rgba(75,70,92,0.08)]">
          <img src="/assets/logodark.webp" alt="DITEC" className="h-12 w-auto" />
          <h1 className="mt-6 text-xl font-semibold text-[#4B465C]">Welcome to DITEC Admin</h1>
          <p className="mt-1.5 text-sm text-[#A5A3AE]">Enter your access key to manage the website.</p>
          <input
            data-testid="admin-key-input"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin access key"
            className="mt-6 w-full rounded-lg border border-[#DBDADE] bg-white px-4 py-3 text-sm text-[#4B465C] outline-none transition-colors focus:border-[#7367F0] placeholder:text-[#B9B7C0]"
          />
          {error && <p data-testid="admin-error" className="mt-2 text-xs text-red-500">{error}</p>}
          <button data-testid="admin-login-button" className="mt-4 w-full touch-target rounded-lg bg-[#7367F0] py-3 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(115,103,240,0.4)] transition-all hover:bg-[#5E50EE] hover:shadow-[0_6px_18px_rgba(115,103,240,0.5)]">
            Sign In
          </button>
        </form>
      </div>
    );
  }

  const Active = ACTIVE[section];

  return (
    <div data-testid="admin-dashboard" className="admin-shell min-h-screen">
      {/* Desktop sidebar */}
      <div className="fixed inset-y-0 left-0 z-40 hidden border-r border-[#EBE9F1] lg:block">
        <Sidebar section={section} setSection={setSection} />
      </div>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="admin-mobile-drawer">
          <div className="absolute inset-0 bg-[#4B465C]/40 backdrop-blur-sm" onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 shadow-2xl">
            <Sidebar section={section} setSection={setSection} onClose={() => setDrawer(false)} />
          </div>
        </div>
      )}

      <div className="flex min-h-screen flex-col lg:pl-[264px]">
        {/* Topbar */}
        <header className="sticky top-0 z-30 border-b border-[#EBE9F1] bg-white/90 backdrop-blur-xl">
          <div className="flex items-center gap-3 px-4 py-3 sm:px-6">
            <button data-testid="admin-menu-button" onClick={() => setDrawer(true)} aria-label="Open menu" className="flex touch-target items-center justify-center text-[#4B465C] lg:hidden">
              <Menu className="h-5 w-5" />
            </button>
            <div className="relative max-w-md flex-1">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#B9B7C0]" />
              <input
                data-testid="admin-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && suggestions[0]) { setSection(suggestions[0].id); setQuery(''); } }}
                placeholder="Search sections…"
                className="w-full rounded-lg border border-transparent bg-[#F8F7FA] py-2.5 pl-10 pr-4 text-sm text-[#4B465C] outline-none transition-colors focus:border-[#7367F0] focus:bg-white placeholder:text-[#B9B7C0]"
              />
              {suggestions.length > 0 && (
                <div className="absolute inset-x-0 top-full mt-1.5 overflow-hidden rounded-lg border border-[#EBE9F1] bg-white shadow-lg">
                  {suggestions.map((s) => (
                    <button key={s.id} data-testid={`search-goto-${s.id}`} onClick={() => { setSection(s.id); setQuery(''); }} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-[#4B465C] hover:bg-[#F8F7FA]">
                      <s.icon className="h-4 w-4 text-[#7367F0]" /> {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="ml-auto flex items-center gap-1.5">
              <span className="relative flex touch-target items-center justify-center rounded-lg text-[#A5A3AE] transition-colors hover:bg-[#F8F7FA]" title="Notifications">
                <Bell className="h-[18px] w-[18px]" />
                <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-[#EA5455]" />
              </span>
              <a href="/" data-testid="admin-view-site" className="flex touch-target items-center justify-center rounded-lg text-[#A5A3AE] transition-colors hover:bg-[#F8F7FA]" title="View site">
                <ExternalLink className="h-[18px] w-[18px]" />
              </a>
              <div className="mx-1.5 hidden h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#7367F0] to-[#9E95F5] text-xs font-bold text-white sm:flex" title="Admin">A</div>
              <button data-testid="admin-logout" onClick={logout} className="flex touch-target items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-[#4B465C] transition-colors hover:bg-[#F8F7FA]">
                <LogOut className="h-4 w-4 text-[#A5A3AE]" /> <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
          {/* Mobile section tabs */}
          <div className="flex gap-1.5 overflow-x-auto px-4 pb-3 lg:hidden">
            {SECTIONS.map((s) => (
              <button key={s.id} data-testid={`admin-tab-${s.id}`} onClick={() => setSection(s.id)} className={`whitespace-nowrap rounded-lg px-3.5 py-2 text-xs font-semibold transition-colors ${section === s.id ? 'bg-[#7367F0] text-white shadow-sm' : 'bg-[#F8F7FA] text-[#4B465C]'}`}>
                {s.label}
              </button>
            ))}
          </div>
        </header>

        <main className="flex-1 p-4 sm:p-7">
          <div key={section} className="animate-[fadeIn_.3s_ease]">
            <Active adminKey={sessionStorage.getItem('ditec_admin_key')} onAuthError={() => { logout(); setError('Invalid admin key.'); }} />
          </div>
        </main>
      </div>
    </div>
  );
}
